/**
 * StreamingPiP - Simple Video Display Component
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { MdClose, MdVideocam, MdVolumeOff, MdZoomInMap, MdZoomOutMap } from 'react-icons/md';
import { Box, IconButton, Tooltip } from '@chakra-ui/react';

// ==================== TYPES ====================

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';
export type PiPTheme = 'mini' | 'normal';

export interface StreamingPiPProps {
  availableThemes?: PiPTheme[];
  isDraggable?: boolean;
  connectionState: ConnectionState;
  hasVideo?: boolean;
  hasAudio?: boolean;
  isAudioMuted?: boolean;
  basePosition?: { top?: number; right?: number; left?: number; bottom?: number };
  onClose?: () => void;
  onUnmuteAudio?: () => void;
  onThemeChange?: (newTheme: PiPTheme) => void;
  onAttachVideo?: (videoElement: HTMLVideoElement) => (() => void) | undefined;
}

// ==================== COMPONENT ====================

export const StreamingPiP: React.FC<StreamingPiPProps> = ({
  availableThemes = ['normal'],
  isDraggable = false,
  connectionState,
  hasVideo = false,
  hasAudio = false,
  isAudioMuted = true,
  basePosition = { top: 20, right: 20, left: 0, bottom: 0 },
  onClose,
  onUnmuteAudio,
  onThemeChange,
  onAttachVideo,
}) => {
  // ==================== STATE ====================

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | undefined>(undefined); // Only for draggable mode
  const [currentTheme, setCurrentTheme] = useState(availableThemes[0] || 'normal');

  // ==================== COMPUTED PROPERTIES ====================

  // Use first theme in availableThemes as current theme
  const isMini = currentTheme === 'mini';
  const isConnected = connectionState === 'connected';
  const isConnecting = connectionState === 'connecting';
  const isError = connectionState === 'error';
  const canSwitchTheme = availableThemes.length > 1 && onThemeChange;
  const allowDrag = isDraggable && !isMini;

  // Calculate position and bounds
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const marginX = isMini ? 50 : 150; // based on theme size
  const marginY = isMini ? 50 : 100; // based on theme size

  const draggableBounds = useMemo(
    () => ({
      left: marginX,
      right: dimensions.width - marginX,
      top: marginY,
      bottom: dimensions.height - marginY,
    }),
    [marginX, marginY, dimensions]
  );

  const defaultPosition = useMemo(() => {
    const secureBasePosition = {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      ...basePosition,
    };
    return {
      x:
        secureBasePosition.right !== 0
          ? dimensions.width - secureBasePosition.right - marginX
          : secureBasePosition.left + marginX,
      y:
        secureBasePosition.bottom !== 0
          ? dimensions.height - secureBasePosition.bottom - marginY
          : secureBasePosition.top + marginY,
    };
  }, [basePosition, dimensions, marginX, marginY]);

  const tooltipLabel = useMemo(() => {
    if (isError) return 'Error';
    if (isConnecting) return 'Connecting';
    return 'Disconnected';
  }, [isError, isConnecting]);

  // ==================== METHODS ====================

  const handleThemeToggle = useCallback(() => {
    if (!canSwitchTheme) return;

    const currentIndex = availableThemes.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % availableThemes.length;
    const newTheme = availableThemes[nextIndex];

    setCurrentTheme(newTheme);
    onThemeChange?.(newTheme);
  }, [currentTheme, availableThemes, canSwitchTheme, onThemeChange]);

  const handleDragStop = useCallback((_e: unknown, data: { x: number; y: number }) => {
    setDragPosition({ x: data.x, y: data.y });
  }, []);

  // ==================== EFFECTS ====================

  // Attach video - trigger when connection state changes to connected
  useEffect(() => {
    if (videoRef.current && onAttachVideo && connectionState === 'connected') {
      console.log('📹 StreamingPiP: Attaching video element (connection established)');
      const cleanup = onAttachVideo(videoRef.current);
      return cleanup || (() => {});
    }
  }, [onAttachVideo, connectionState]); // Only re-attach when connection changes

  // initialization of position for pip if dimensions are ready and default position also
  useEffect(() => {
    if (!dragPosition && defaultPosition && dimensions.width > 0 && dimensions.height > 0) {
      setDragPosition(defaultPosition);
    }
  }, [defaultPosition, dragPosition, dimensions]);

  // ==================== RENDER ====================

  // Container content
  const videoContent = (
    <Box
      width={isMini ? '100px' : '300px'}
      height={isMini ? '100px' : '200px'}
      cursor={allowDrag ? 'move' : 'default'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      transform="translate(-50%, -50%)"
      transition="all 0.25s ease-out"
    >
      <Box
        width="200%"
        height="200%"
        overflow="hidden"
        transform="translate(-25%, -25%) scale(0.5)"
        bg="black"
        borderRadius={isMini ? '50%' : '16px'}
        border={`2px solid ${isConnected ? 'white' : '#ff6b6b'}`}
        boxShadow="lg"
      >
        {/* Video */}
        <video
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          autoPlay
          muted={isAudioMuted}
          playsInline
        >
          <track kind="captions" />
        </video>
      </Box>

      {/* Placeholder when no video */}
      {!hasVideo && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          color="white"
        >
          <MdVideocam size={24} />
        </Box>
      )}

      {/* Connection status */}
      {!isConnected && (
        <Tooltip label={tooltipLabel}>
          <Box
            position="absolute"
            top="4px"
            right="4px"
            // bg={isError ? 'red.500' : 'orange.500'}
            color="white"
            px={2}
            py={1}
            borderRadius="full"
            fontSize="xs"
          >
            {(() => {
              if (isError) return '❌';
              if (isConnecting) return '...';
              return '⚠️';
            })()}
          </Box>
        </Tooltip>
      )}

      {/* Close button */}
      {onClose && (
        <IconButton
          aria-label="Close"
          icon={<MdClose />}
          size="xs"
          position="absolute"
          top="4px"
          left="4px"
          bg="rgba(0,0,0,0.7)"
          color="white"
          opacity={isHovered ? 1 : 0.3}
          _hover={{ opacity: 1 }}
          onClick={onClose}
        />
      )}

      {/* Theme switcher */}
      {canSwitchTheme && (
        <IconButton
          aria-label="Switch theme"
          icon={isMini ? <MdZoomOutMap /> : <MdZoomInMap />}
          size="xs"
          position="absolute"
          borderRadius="full"
          top="4px"
          right="4px"
          bg="rgba(0,0,0,0.7)"
          color="white"
          opacity={isHovered ? 1 : 0}
          _hover={{ opacity: 1 }}
          onClick={handleThemeToggle}
        />
      )}

      {/* Unmute button */}
      {hasAudio && isAudioMuted && onUnmuteAudio && (
        <IconButton
          aria-label="Unmute"
          icon={<MdVolumeOff />}
          size="xs"
          position="absolute"
          bottom="4px"
          right="4px"
          bg="rgba(0,0,0,0.7)"
          borderRadius="full"
          color="white"
          opacity={isHovered ? 1 : 0}
          _hover={{ opacity: 1 }}
          onClick={onUnmuteAudio}
        />
      )}
    </Box>
  );

  // render
  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      width="100vw"
      height="100vh"
      zIndex={1000}
      pointerEvents="none"
    >
      <Draggable
        onStop={handleDragStop}
        bounds={draggableBounds}
        disabled={!allowDrag}
        position={allowDrag ? dragPosition : defaultPosition}
      >
        <div style={{ position: 'absolute', pointerEvents: 'auto' }}>{videoContent}</div>
      </Draggable>
    </Box>
  );
};
